import React, { useCallback, useEffect, useState } from 'react';
import { getApiErrorText } from '../../../../utils/apiResponse';
import { incomingShipmentApi } from '../../../../api/incomingShipmentApi';

const emptyMapping = {
  article: '',
  name: '',
  quantity: '',
  price: '',
  barcode: ''
};

/**
 * Імпорт очікуваних товарів з Excel: лист, рядок заголовків, мапінг колонок, preview, підтвердження.
 */
const IncomingShipmentExcelImportModal = ({ open, shipmentId, onClose, onImported, api = incomingShipmentApi }) => {
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRowNumber, setHeaderRowNumber] = useState(1);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState(() => ({ ...emptyMapping }));
  const [preview, setPreview] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [error, setError] = useState('');

  const resetState = useCallback(() => {
    setFile(null);
    setSheets([]);
    setSheetIndex(0);
    setHeaderRowNumber(1);
    setColumns([]);
    setMapping({ ...emptyMapping });
    setPreview(null);
    setError('');
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    const onKey = (e) => {
      if (e.key === 'Escape' && !metaLoading && !columnsLoading && !previewLoading && !applyLoading) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, metaLoading, columnsLoading, previewLoading, applyLoading, resetState]);

  const loadMetadata = async (f) => {
    setMetaLoading(true);
    setError('');
    setPreview(null);
    setColumns([]);
    setMapping({ ...emptyMapping });
    try {
      const res = await api.excelImportMetadata(shipmentId, f);
      const list = res.data?.data?.sheets || [];
      setSheets(list);
      setSheetIndex(0);
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося імпортувати поставку'));
      setSheets([]);
    } finally {
      setMetaLoading(false);
    }
  };

  const onPickFile = (e) => {
    const input = e.currentTarget;
    const f = input?.files?.[0];
    if (input) {
      input.value = '';
    }
    if (!f) return;
    const n = f.name?.toLowerCase() || '';
    if (!n.endsWith('.xlsx') && !n.endsWith('.xls')) {
      setError('Потрібен файл Excel (.xlsx або .xls)');
      return;
    }
    setFile(f);
    loadMetadata(f);
  };

  const loadColumns = async () => {
    if (!file) return;
    const hr = Math.max(1, parseInt(headerRowNumber, 10) || 1);
    setColumnsLoading(true);
    setError('');
    setPreview(null);
    try {
      const res = await api.excelImportColumns(shipmentId, file, sheetIndex, hr - 1);
      setColumns(res.data?.data?.columns || []);
      setMapping({ ...emptyMapping });
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося імпортувати поставку'));
      setColumns([]);
    } finally {
      setColumnsLoading(false);
    }
  };

  const appendMapping = (fd) => {
    const hr = Math.max(1, parseInt(headerRowNumber, 10) || 1);
    fd.append('sheetIndex', String(sheetIndex));
    fd.append('headerRowIndex', String(hr - 1));
    fd.append('articleColumnIndex', String(mapping.article));
    fd.append('nameColumnIndex', String(mapping.name));
    fd.append('quantityColumnIndex', String(mapping.quantity));
    if (mapping.price !== '' && mapping.price != null) {
      fd.append('priceColumnIndex', String(mapping.price));
    }
    if (mapping.barcode !== '' && mapping.barcode != null) {
      fd.append('barcodeColumnIndex', String(mapping.barcode));
    }
  };

  const runPreview = async () => {
    if (!file) return;
    if (mapping.article === '' || mapping.name === '' || mapping.quantity === '') {
      setError('Оберіть колонки для артикулу, назви та кількості');
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      appendMapping(fd);
      const res = await api.excelImportPreview(shipmentId, fd);
      setPreview(res.data?.data || null);
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося імпортувати поставку'));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!file || !preview || !preview.totalRowsToImport) {
      setError('Спочатку виконайте перегляд і переконайтесь, що є рядки для імпорту');
      return;
    }
    setApplyLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      appendMapping(fd);
      const res = await api.excelImportApply(shipmentId, fd);
      const data = res.data?.data;
      const payload = Array.isArray(data) ? { expectedItems: data } : (data || {});
      onImported(payload);
      onClose();
    } catch (e) {
      setError(getApiErrorText(e, 'Не вдалося імпортувати поставку'));
    } finally {
      setApplyLoading(false);
    }
  };

  const colOptions = (includeEmpty) => (
    <>
      {includeEmpty ? <option value=''>— не використовувати —</option> : null}
      {(columns || []).map((c) => (
        <option key={c.columnIndex} value={String(c.columnIndex)}>
          {c.headerLabel} (#{c.columnIndex})
        </option>
      ))}
    </>
  );

  if (!open) return null;

  const busy = metaLoading || columnsLoading || previewLoading || applyLoading;

  return (
    <div
      className='incoming-excel-import-backdrop'
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={() => !busy && onClose()}
      role='presentation'
    >
      <div
        className='shadow bg-white rounded'
        style={{
          maxWidth: 'min(1120px, calc(100vw - 2rem))',
          width: '100%',
          maxHeight: '92vh',
          overflow: 'auto',
          padding: '1rem'
        }}
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        data-incoming-expected-block='true'
      >
        <h5 className='font-weight-bold mb-2'>Імпорт з файлу (Excel)</h5>
        <p className='text-muted small mb-3'>
          Завантажте файл, вкажіть лист і рядок заголовків, оберіть відповідність колонок, перегляньте дані та підтвердіть імпорт.
        </p>

        {error ? <div role='alert' className='alert alert-danger py-2 small mb-3'>{error}</div> : null}

        <div className='form-group mb-2'>
          <label className='small font-weight-bold d-block'>Файл</label>
          <input type='file' accept='.xlsx,.xls' onChange={onPickFile} disabled={busy} className='form-control-file' />
          {metaLoading ? <div className='small text-muted mt-1'>Читання файлу…</div> : null}
          {file ? <div className='small text-muted mt-1'>Обрано: {file.name}</div> : null}
        </div>

        {sheets.length > 1 ? (
          <div className='form-group mb-2'>
            <label className='small font-weight-bold d-block'>Лист</label>
            <select
              className='form-control form-control-sm'
              value={sheetIndex}
              onChange={(e) => {
                setSheetIndex(parseInt(e.currentTarget.value, 10));
                setColumns([]);
                setMapping({ ...emptyMapping });
                setPreview(null);
              }}
              disabled={busy || !file}
            >
              {sheets.map((s) => (
                <option key={s.sheetIndex} value={s.sheetIndex}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className='form-row align-items-end mb-2'>
          <div className='col-md-4 mb-2 mb-md-0'>
            <label className='small font-weight-bold d-block'>Рядок заголовків (Excel)</label>
            <input
              type='number'
              min={1}
              className='form-control form-control-sm'
              value={headerRowNumber}
              onChange={(e) => setHeaderRowNumber(e.currentTarget.value)}
              disabled={busy || !file}
            />
          </div>
          <div className='col-md-8'>
            <button type='button' className='btn btn-sm btn-outline-primary' onClick={loadColumns} disabled={busy || !file}>
              {columnsLoading ? 'Завантаження…' : 'Завантажити список колонок'}
            </button>
          </div>
        </div>

        {columns.length > 0 ? (
          <div className='border rounded p-2 mb-3 small'>
            <div className='font-weight-bold mb-2'>Відповідність колонок</div>
            <div className='form-group mb-2'>
              <label className='mb-0 d-block'>Артикул</label>
              <select
                className='form-control form-control-sm'
                value={mapping.article}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMapping((m) => ({ ...m, article: v }));
                }}
                disabled={busy}
              >
                <option value=''>Оберіть колонку</option>
                {colOptions(false)}
              </select>
            </div>
            <div className='form-group mb-2'>
              <label className='mb-0 d-block'>Назва товару</label>
              <select
                className='form-control form-control-sm'
                value={mapping.name}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMapping((m) => ({ ...m, name: v }));
                }}
                disabled={busy}
              >
                <option value=''>Оберіть колонку</option>
                {colOptions(false)}
              </select>
            </div>
            <div className='form-group mb-2'>
              <label className='mb-0 d-block'>Кількість</label>
              <select
                className='form-control form-control-sm'
                value={mapping.quantity}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMapping((m) => ({ ...m, quantity: v }));
                }}
                disabled={busy}
              >
                <option value=''>Оберіть колонку</option>
                {colOptions(false)}
              </select>
            </div>
            <div className='form-group mb-2'>
              <label className='mb-0 d-block'>Ціна закупки</label>
              <select
                className='form-control form-control-sm'
                value={mapping.price}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMapping((m) => ({ ...m, price: v }));
                }}
                disabled={busy}
              >
                {colOptions(true)}
              </select>
            </div>
            <div className='form-group mb-0'>
              <label className='mb-0 d-block'>Штрихкод (необовʼязково)</label>
              <select
                className='form-control form-control-sm'
                value={mapping.barcode}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMapping((m) => ({ ...m, barcode: v }));
                }}
                disabled={busy}
              >
                {colOptions(true)}
              </select>
            </div>
          </div>
        ) : null}

        <div className='d-flex flex-wrap mb-3' style={{ gap: '0.5rem' }}>
          <button type='button' className='btn btn-sm btn-outline-secondary' onClick={runPreview} disabled={busy || !columns.length}>
            {previewLoading ? 'Перегляд…' : 'Перегляд'}
          </button>
        </div>

        {preview ? (
          <div className='mb-3'>
            <div className='small text-muted mb-1'>
              До імпорту: <strong>{preview.totalRowsToImport}</strong> рядків; пропущено (порожні / без кількості):{' '}
              <strong>{preview.skippedRows}</strong>
            </div>
            <div className='table-responsive border rounded' style={{ maxHeight: '240px', overflow: 'auto' }}>
              <table className='table table-sm table-striped mb-0 small'>
                <thead>
                  <tr>
                    <th># Excel</th>
                    <th>Артикул</th>
                    <th>Назва</th>
                    <th>К-ть</th>
                    <th>Ціна</th>
                    <th>Штрихкод</th>
                    <th>ID в каталозі</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.previewRows || []).map((r, i) => (
                    <tr key={`${r.excelRowNumber}-${i}`}>
                      <td>{r.excelRowNumber}</td>
                      <td className='text-monospace'>{r.productCode || '—'}</td>
                      <td>{r.name || '—'}</td>
                      <td>{r.quantity}</td>
                      <td>
                        {r.purchasePrice != null ? r.purchasePrice : r.priceCouldNotParse ? <span className='text-warning'>не розпізнано</span> : '—'}
                      </td>
                      <td className='text-monospace'>{r.barcode || '—'}</td>
                      <td>{r.catalogMatchedProductId != null ? r.catalogMatchedProductId : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(preview.previewRows || []).length < preview.totalRowsToImport ? (
              <div className='small text-muted mt-1'>Показано перші {preview.previewRows.length} з {preview.totalRowsToImport}</div>
            ) : null}
          </div>
        ) : null}

        <div className='d-flex justify-content-end flex-wrap' style={{ gap: '0.5rem' }}>
          <button type='button' className='btn btn-sm btn-outline-secondary' onClick={onClose} disabled={busy}>
            Скасувати
          </button>
          <button
            type='button'
            className='btn btn-sm btn-dark'
            onClick={runApply}
            disabled={busy || !preview || !preview.totalRowsToImport}
          >
            {applyLoading ? 'Імпорт…' : 'Імпортувати'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingShipmentExcelImportModal;
